import { TResponse } from 'core/classes/ws/WebSocketClient'
import { isInstanceOf } from 'core/utils/functions'
import { AggregateWorkflowError } from 'onboarding/classes/errors/AggregateWorkflowError'
import { AlreadyExistsError } from 'onboarding/classes/errors/AlreadyExistsError'
import { AppError } from 'onboarding/classes/errors/AppError'
import { ExpiredError } from 'onboarding/classes/errors/ExpiredError'
import { InvalidChangePartnerError } from 'onboarding/classes/errors/InvalidChangePartnerError'
import { LinkedSpecializationError } from 'onboarding/classes/errors/LinkedSpecializationError'
import { PartnerNotFoundError } from 'onboarding/classes/errors/PartnerNotFoundError'
import { ValidationParamsError } from 'onboarding/classes/errors/ValidationParamsError'
import { IValidationParamsErrorPayload } from 'onboarding/types/request'
import {
  IAlreadyExistsErrorResponsePayload,
  IExpiredErrorResponsePayload,
  IInvalidChangePartnerErrorResponsePayload,
  IInvalidParamsErrorResponsePayload,
  ILinkedSpecializationErrorResponsePayload,
  IPartnerNotFoundErrorResponsePayload,
  IResponseErrorPayload,
} from 'onboarding/types/response'

export const throwOnErrorWithoutPayload = (
  response: TResponse<object>,
  fallbackMessage?: string
) => {
  throw new AppError(response.message || fallbackMessage || 'Error')
}

export function throwOnAlreadyExists(
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage?: string
) {
  const { errorPayload } = response.data
  const resultMessage = response.message || fallbackMessage || 'Already exists'
  if (
    errorPayload &&
    isInstanceOf<IAlreadyExistsErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'alreadyExists'
    )
  ) {
    throw new AlreadyExistsError(resultMessage, errorPayload.existingId, errorPayload.additional)
  }
}

export const throwOnInvalidRequestParams = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
    /** @deprecated use typed errorPayload */
    error?: any
  }>,
  fallbackMessage?: string
) => {
  const { message, data } = response
  const { errorPayload, error } = data
  const resultMessage = message || fallbackMessage || 'Invalid parameters'
  if (
    errorPayload &&
    isInstanceOf<IInvalidParamsErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'invalidParams' && o.validatorResult && Array.isArray(o.validatorResult)
    )
  ) {
    throw new ValidationParamsError(resultMessage, errorPayload.validatorResult)
  }

  if (error && Array.isArray(error)) {
    const validationErrors = error.filter((i) =>
      isInstanceOf<IValidationParamsErrorPayload>(i, 'message')
    )
    if (validationErrors.length) {
      throw new ValidationParamsError(resultMessage, validationErrors)
    }
  }
}

export const throwOnAggregateWorkflowError = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage?: string
) => {
  const { message, data } = response
  const { errorPayload } = data
  if (
    errorPayload &&
    errorPayload.type === 'aggregateWorkflowError' &&
    errorPayload.errors &&
    Array.isArray(errorPayload.errors)
  ) {
    throw new AggregateWorkflowError(
      message || fallbackMessage || 'Workflow Error',
      errorPayload.errors
    )
  }
}

export const throwOnExpiredError = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage?: string
) => {
  const { message, data } = response
  const { errorPayload } = data
  if (
    errorPayload &&
    isInstanceOf<IExpiredErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'expired' && 'amount' in o && 'unit' in o
    )
  ) {
    throw new ExpiredError(
      message || fallbackMessage || 'Expired',
      errorPayload.amount,
      errorPayload.unit
    )
  }
}

export const throwOnInvalidChangePartnerError = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage: string
) => {
  const { message, data } = response
  const { errorPayload } = data
  if (
    errorPayload &&
    isInstanceOf<IInvalidChangePartnerErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'invalidChangePartner'
    )
  ) {
    throw new InvalidChangePartnerError(message || fallbackMessage)
  }
}

export const throwOnPartnerNotFoundError = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage: string
) => {
  const { message, data } = response
  const { errorPayload } = data
  if (
    errorPayload &&
    isInstanceOf<IPartnerNotFoundErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'partnerNotFound'
    )
  ) {
    throw new PartnerNotFoundError(message || fallbackMessage, errorPayload)
  }
}

export const throwOnLinkedSpecializationError = (
  response: TResponse<{
    errorPayload?: IResponseErrorPayload
  }>,
  fallbackMessage: string
) => {
  const { message, data } = response
  const { errorPayload } = data
  if (
    errorPayload &&
    isInstanceOf<ILinkedSpecializationErrorResponsePayload>(
      errorPayload,
      (o) => o.type === 'linkedSpecializationError'
    )
  ) {
    throw new LinkedSpecializationError(
      message || fallbackMessage,
      errorPayload.linkedRequestTemplates
    )
  }
}
